import type { FC, ReactNode } from 'react'
import type { SortState } from '@/lib/list'
import type { Treatment } from '@/types/clinic'

import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Pencil, Plus, SearchX, Stethoscope, Trash2 } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { useClinic } from '@/lib/clinic/store'
import { sortItems, toggleSort } from '@/lib/list'
import { useMoney } from '@/hooks/use-clinic-helpers'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import PriceCell from '@/components/treatments/price-cell'
import PageHeader from '@/components/app/page-header'
import PageLoading from '@/components/app/page-loading'
import SearchInput from '@/components/app/search-input'
import SimpleSelect from '@/components/app/simple-select'
import SortableHead from '@/components/app/sortable-head'
import EmptyState from '@/components/app/empty-state'
import IconAction from '@/components/app/icon-action'
import ConfirmDialog from '@/components/app/confirm-dialog'
import TreatmentDialog from '@/components/treatments/treatment-dialog'

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table'

type SortKey = 'name' | 'price' | 'duration' | 'usage'

const TreatmentsPage: FC = (): ReactNode => {
    const { ready, data, actions } = useClinic()
    const money = useMoney()
    const [query, setQuery] = useState('')
    const [status, setStatus] = useState('all')
    const [sort, setSort] = useState<SortState<SortKey>>({ key: 'name', direction: 'asc' })
    const [dialog, setDialog] = useState<{ open: boolean, key: number, treatment?: Treatment }>({ open: false, key: 0 })
    const [toDelete, setToDelete] = useState<Treatment | null>(null)

    const usage = useMemo(() => {
        const counts = new Map<string, number>()
        for (const visit of data.visits) {
            for (const ref of visit.treatments) {
                if (ref.treatmentId) counts.set(ref.treatmentId, (counts.get(ref.treatmentId) ?? 0) + 1)
            }
        }
        return counts
    }, [data.visits])

    const rows = useMemo(() => {
        const value = query.trim().toLowerCase()
        const filtered = data.treatments.filter(item => {
            if (status === 'active' && !item.active) return false
            if (status === 'inactive' && item.active) return false
            return !value || item.name.toLowerCase().includes(value) || item.description.toLowerCase().includes(value)
        })
        const getter = (item: Treatment): unknown => {
            if (sort.key === 'price') return item.price
            if (sort.key === 'duration') return item.duration
            if (sort.key === 'usage') return usage.get(item.id) ?? 0
            return item.name
        }
        return sortItems(filtered, getter, sort.direction)
    }, [data.treatments, query, status, sort, usage])

    const openDialog = (treatment?: Treatment) => setDialog(current => ({ open: true, key: current.key + 1, treatment }))

    if (!ready) {
        return (
            <>
                <Seo title='Treatments' />
                <PageLoading />
            </>
        )
    }

    const activeCount = data.treatments.filter(item => item.active).length

    return (
        <>
            <Seo title='Treatments' description='Manage dental treatments, prices and availability.' />

            <PageHeader
                title='Treatments'
                description={`${activeCount} active of ${data.treatments.length} treatments. Click a price to change it.`}
                actions={(
                    <Button onClick={() => openDialog()}>
                        <Plus aria-hidden='true' />
                        Add treatment
                    </Button>
                )}
            />

            <Card className='gap-0 py-0'>
                <div className='grid gap-3 border-b p-4 sm:grid-cols-[minmax(0,1fr)_12rem]'>
                    <SearchInput value={query} onChange={setQuery} placeholder='Search treatments' />
                    <SimpleSelect
                        value={status}
                        onValueChange={setStatus}
                        options={[
                            { value: 'all', label: 'All treatments' },
                            { value: 'active', label: 'Active only' },
                            { value: 'inactive', label: 'Inactive only' }
                        ]}
                        ariaLabel='Filter by status'
                    />
                </div>

                {rows.length === 0
                    ? (
                        data.treatments.length === 0
                            ? <EmptyState icon={Stethoscope} title='No treatments yet' description='Add the treatments your clinic offers.' action={<Button onClick={() => openDialog()}>Add treatment</Button>} />
                            : <EmptyState icon={SearchX} title='No treatments match' description='Try another search or filter.' />
                    )
                    : (
                        <Table>
                            <TableHeader>
                                <TableRow className='hover:bg-transparent'>
                                    <SortableHead label='Treatment' sortKey='name' sort={sort} onSort={key => setSort(current => toggleSort(current, key))} className='pl-4' />
                                    <SortableHead label='Price' sortKey='price' sort={sort} onSort={key => setSort(current => toggleSort(current, key))} align='right' />
                                    <SortableHead label='Duration' sortKey='duration' sort={sort} onSort={key => setSort(current => toggleSort(current, key))} className='hidden md:table-cell' />
                                    <SortableHead label='Performed' sortKey='usage' sort={sort} onSort={key => setSort(current => toggleSort(current, key, 'desc'))} className='hidden sm:table-cell' />
                                    <TableHead className='text-xs font-semibold tracking-wide text-muted-foreground uppercase'>Active</TableHead>
                                    <TableHead className='pr-4 text-right'><span className='sr-only'>Actions</span></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map(treatment => (
                                    <TableRow key={treatment.id} className={treatment.active ? '' : 'bg-muted/30'}>
                                        <TableCell className='max-w-md pl-4 whitespace-normal'>
                                            <p className={`font-medium ${treatment.active ? 'text-foreground' : 'text-muted-foreground'}`}>{treatment.name}</p>
                                            {treatment.description && <p className='mt-0.5 line-clamp-2 text-xs text-muted-foreground'>{treatment.description}</p>}
                                        </TableCell>
                                        <TableCell className='text-right'>
                                            <PriceCell
                                                treatment={treatment}
                                                onSave={price => {
                                                    actions.updateTreatment(treatment.id, { price })
                                                    toast.success(`${treatment.name} now costs ${money(price)}`)
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell className='hidden text-muted-foreground md:table-cell'>{treatment.duration} min</TableCell>
                                        <TableCell className='hidden text-muted-foreground sm:table-cell'>{usage.get(treatment.id) ?? 0} times</TableCell>
                                        <TableCell>
                                            <Switch
                                                checked={treatment.active}
                                                onCheckedChange={checked => {
                                                    actions.updateTreatment(treatment.id, { active: checked })
                                                    toast.success(`${treatment.name} ${checked ? 'activated' : 'deactivated'}`)
                                                }}
                                                aria-label={`${treatment.active ? 'Deactivate' : 'Activate'} ${treatment.name}`}
                                            />
                                        </TableCell>
                                        <TableCell className='pr-4 text-right'>
                                            <div className='flex justify-end'>
                                                <IconAction label='Edit treatment' icon={Pencil} onClick={() => openDialog(treatment)} />
                                                <IconAction label='Delete treatment' icon={Trash2} tone='danger' onClick={() => setToDelete(treatment)} />
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
            </Card>

            <TreatmentDialog
                key={dialog.key}
                open={dialog.open}
                onOpenChange={open => setDialog(current => ({ ...current, open }))}
                treatment={dialog.treatment}
            />

            <ConfirmDialog
                open={Boolean(toDelete)}
                onOpenChange={open => !open && setToDelete(null)}
                title={`Delete ${toDelete?.name ?? 'treatment'}?`}
                description='It will no longer be available for new visits, appointments or bills. Past visits and bills keep their records. To hide it temporarily, switch it off instead.'
                confirmLabel='Delete treatment'
                onConfirm={() => {
                    if (!toDelete) return
                    actions.deleteTreatment(toDelete.id)
                    toast.success('Treatment deleted')
                }}
            />
        </>
    )
}

export default TreatmentsPage
